import { useState } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import { guardUnsavedBack } from '../../../lib/formGuard';
import type { PastMedicalHistory } from '../../../types/prf';

interface PastMedicalHistorySectionProps {
  data: PastMedicalHistory;
  onSave: (data: PastMedicalHistory) => void;
  onBack: () => void;
}

export default function PastMedicalHistorySection({
  data,
  onSave,
  onBack,
}: PastMedicalHistorySectionProps) {
  const [formData, setFormData] = useState<PastMedicalHistory>(data);

  const handleChange = (field: keyof PastMedicalHistory, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave(formData);
  };

  const handleBackClick = () => guardUnsavedBack(data, formData, onBack);

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackClick}
              className="p-1.5 rounded-lg hover:bg-accent transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-foreground">Past Medical History</h2>
              <p className="text-xs text-muted-foreground">Previous conditions and medical background</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Conditions */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Conditions
            </label>
            <textarea
              value={Array.isArray(formData.conditions) ? formData.conditions.join('\n') : formData.conditions || ''}
              onChange={(e) => handleChange('conditions', e.target.value.split('\n').filter(s => s.trim()))}
              rows={4}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
              placeholder="Enter conditions (one per line)"
            />
          </div>

          {/* Surgeries */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Surgeries
            </label>
            <textarea
              value={Array.isArray(formData.surgeries) ? formData.surgeries.join('\n') : formData.surgeries || ''}
              onChange={(e) => handleChange('surgeries', e.target.value.split('\n').filter(s => s.trim()))}
              rows={4}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
              placeholder="Enter surgeries (one per line)"
            />
          </div>

          {/* Hospitalisations */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Hospitalisations
            </label>
            <textarea
              value={Array.isArray(formData.hospitalisations) ? formData.hospitalisations.join('\n') : formData.hospitalisations || ''}
              onChange={(e) => handleChange('hospitalisations', e.target.value.split('\n').filter(s => s.trim()))}
              rows={4}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
              placeholder="Enter hospitalisations (one per line)"
            />
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Additional Notes
            </label>
            <textarea
              value={formData.additionalNotes || ''}
              onChange={(e) => handleChange('additionalNotes', e.target.value)}
              rows={4}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
              placeholder="Any additional medical history information..."
            />
          </div>
        </div>
      </div>

      {/* Footer with Save Button */}
      <div className="flex-shrink-0 border-t border-border bg-card p-4">
        <button
          onClick={handleSave}
          className="w-full py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold text-sm transition-all flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4" />
          Save
        </button>
      </div>
    </div>
  );
}
